import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/utils";

// Import images
import bgImg1 from "@/asset/images/showcase/1.png";
import bgImg2 from "@/asset/images/showcase/2.png";
import bgImg3 from "@/asset/images/showcase/3.png";
import bgImg4 from "@/asset/images/showcase/4.png";
import bgImg5 from "@/asset/images/showcase/5.png";
import CheckIcon from "@/asset/icons/check-icon.svg";

interface Project {
  id: string;
  title: string;
  description: string;
  image: any;
  objective: string[];
  scope: string[];
  results: { value: string; label: string }[];
  className: string;
}

const showcaseProjects: Project[] = [
  {
    id: "over-protocol",
    title: "Over Protocol",
    description:
      "Over Protocol is a Layer 1 blockchain platform that enables users to run full nodes on personal computers, fostering a decentralized and inclusive network.",
    image: bgImg1,
    objective: [
      "Increase user engagement and onboard new users.",
      "Enhance brand awareness and credibility.",
    ],
    scope: [
      "Social media campaigns across X (formerly Twitter), Instagram, and LinkedIn, including strategy development, content creation, and community management.",
      "Organic media outreach and publications.",
    ],
    results: [
      { value: "150K+", label: "Impressions" },
      { value: "4.2%", label: "X engagement rate" },
      { value: "80K+", label: "Influencer campaign views" },
    ],
    className: "border-gradient-3",
  },
  {
    id: "altlayer",
    title: "AltLayer",
    description:
      "AltLayer is a Layer 2 scaling solution that enhances blockchain performance and scalability.",
    image: bgImg2,
    objective: [
      "Inform the community about key updates and announcements.",
      "Manage brand reputation and crisis response.",
    ],
    scope: [
      "In-depth sponsored articles.",
      "Promotion across social media platforms, including Facebook, X, and Telegram.",
    ],
    results: [
      { value: "2.8M+", label: "Users reached" },
      { value: "200K+", label: "Campaign views" },
      { value: "300K+", label: "Social media impressions" },
    ],
    className: "border-gradient-3-2",
  },
  {
    id: "bigtime",
    title: "BigTime",
    description:
      "BigTime is a multiplayer online role-playing game (MMORPG) integrating blockchain technology, allowing players to own and trade in-game assets.",
    image: bgImg3,
    objective: [
      "Boost brand visibility, attract new players, and increase engagement among existing users.",
    ],
    scope: ["In-depth blogs.", "Social media promotion"],
    results: [
      { value: "25K+", label: "Article views" },
      { value: "160K+", label: "Social media reach" },
    ],
    className: "border-gradient-3-4",
  },
  {
    id: "3co",
    title: "3CO",
    description:
      "3CO is a DeFi platform offering innovative investment and savings solutions",
    image: bgImg4,
    objective: [
      "Strengthen brand recognition, engage the community, and attract new users.",
    ],
    scope: [
      "In-depth research articles by Klarda.",
      "Display ads and strategic sponsorships.",
      "AMA sessions and multi-platform content, including X and YouTube.",
    ],
    results: [
      { value: "550K+", label: "Content reach" },
      { value: "2.7%", label: "Click-through rate(CTR)" },
      { value: "1.3M+", label: "Display ad impressions" },
      { value: "140K+", label: "Social media engagement" },
    ],
    className: "border-gradient-3-4",
  },
  {
    id: "btc-media",
    title: "Event Promotion for BTC Media",
    description:
      "BTC Media is the organizer of premier Bitcoin conferences worldwide, including Bitcoin MENA, Bitcoin Amsterdam, and Bitcoin Las Vegas—some of the biggest blockchain events of 2024, featuring industry leaders like CZ, Eric Trump, Michael Saylor, and Simon Gerovich.",
    image: bgImg5,
    objective: [
      "Maximize global visibility and attendance for Bitcoin Amsterdam 2024.",
      "Drive engagement among Bitcoin enthusiasts, investors, and industry leaders.",
    ],
    scope: [
      "Multi-channel social media campaign across X, LinkedIn, and Telegram.",
      "Influencer collaborations and organic media outreach.",
      "On-site event coverage and live engagement strategy.",
    ],
    results: [
      { value: "2.1M+", label: "Total impressions" },
      { value: "450K+", label: "Video views on social platforms" },
      { value: "180K+", label: "Social media interactions" },
      { value: "5.3%", label: "Engagement rate on X" },
      { value: "3K+", label: "Event-related mentions across crypto media" },
    ],
    className: "border-gradient-3-5",
  },
];

export default function ShowcaseSection() {
  // State for active tab and mobile display
  const [mobileTab, setMobileTab] = useState("over-protocol");
  const [previousTab, setPreviousTab] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(
    null
  );
  // New state for expanded/collapsed view in mobile
  const [expandedViews, setExpandedViews] = useState<{
    [key: string]: boolean;
  }>({});
  // State to track if content exceeds threshold width
  const [contentExceedsWidth, setContentExceedsWidth] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Set current project based on active tab
  const currentProject =
    showcaseProjects.find((project) => project.id === mobileTab) ||
    showcaseProjects[0];

  // Get current project index
  const currentIndex = showcaseProjects.findIndex(
    (project) => project.id === mobileTab
  );

  // Check if current project is expanded
  const isCurrentProjectExpanded = expandedViews[mobileTab] || false;

  console.log("isCurrentProjectExpanded", isCurrentProjectExpanded);

  // Toggle expanded view for current project
  const toggleExpand = () => {
    setExpandedViews({
      ...expandedViews,
      [mobileTab]: !isCurrentProjectExpanded,
    });
  };

  // Check window size on component mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    // Set initial value
    checkScreenSize();

    // Add event listener
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Check if content height exceeds threshold (500px)
  useEffect(() => {
    if (isMobile && contentRef.current) {
      const checkContentHeight = () => {
        // Get scrollHeight (total height including overflow) of the card
        const contentHeight = contentRef.current?.scrollHeight || 0;
        // Compare with max visible height
        setContentExceedsWidth(contentHeight > 600);
      };

      // Wait for content to render
      setTimeout(checkContentHeight, 100);

      // Recheck when window resizes
      window.addEventListener("resize", checkContentHeight);

      return () => {
        window.removeEventListener("resize", checkContentHeight);
      };
    }
  }, [isMobile, mobileTab]);

  // Handle tab change for both mobile and desktop
  const handleTabChange = (value: string) => {
    if (isAnimating) return;

    setPreviousTab(mobileTab);
    setMobileTab(value);
  };

  // Handle next slide
  const goToNext = () => {
    if (isAnimating) return;

    const nextIndex = (currentIndex + 1) % showcaseProjects.length;
    setSwipeDirection("right");
    setIsAnimating(true);
    setPreviousTab(mobileTab);
    setMobileTab(showcaseProjects[nextIndex].id);
  };

  // Handle previous slide
  const goToPrev = () => {
    if (isAnimating) return;

    const prevIndex =
      currentIndex === 0 ? showcaseProjects.length - 1 : currentIndex - 1;
    setSwipeDirection("left");
    setIsAnimating(true);
    setPreviousTab(mobileTab);
    setMobileTab(showcaseProjects[prevIndex].id);
  };

  // Touch handlers for swipe on mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    // Store the initial touch position
    setTouchStart(e.targetTouches[0].clientX);
    // Reset touchEnd to match touchStart initially to prevent unintended swipes
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Only update touchEnd if we're definitely doing a swipe gesture
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    // Only trigger navigation if there was significant horizontal movement (swipe)
    // Increase the threshold to make it less sensitive
    const swipeDistance = touchStart - touchEnd;

    // If the swipe distance is small, treat it as a click, not a swipe
    if (Math.abs(swipeDistance) < 20) {
      return;
    }

    if (swipeDistance > 50) {
      // Swipe left (next)
      goToNext();
    }

    if (swipeDistance < -50) {
      // Swipe right (prev)
      goToPrev();
    }
  };

  // Animation variants for mobile slider
  const slideVariants = {
    hiddenLeft: {
      x: "-100%",
      opacity: 0,
    },
    hiddenRight: {
      x: "100%",
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exitLeft: {
      x: "-100%",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exitRight: {
      x: "100%",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
  };

  // Reset animation flag when animation completes
  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  return (
    <section id='cases'>
      <div className='max-w-[1180px] mx-auto flex flex-col justify-center items-center gap-4 py-8'>
        <motion.h2
          className='font-bold text-[24px] md:text-[40px] lg:text-[68px] leading-tight lg:leading-[88px]'
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}>
          Showcase
        </motion.h2>

        {/* Desktop Tabs View */}
        {!isMobile && (
          <Tabs
            defaultValue='over-protocol'
            value={mobileTab}
            onValueChange={handleTabChange}
            className='w-full'>
            {/* Desktop Tabs (hidden on small screens) */}
            <div className='hidden md:flex justify-center mb-4 overflow-x-auto py-2'>
              <TabsList
                className={cn(
                  "rounded-[50px] p-0 h-[50px] md:h-[50px] flex-wrap",
                  currentProject.className // Use the active tab's className for proper gradient
                )}>
                {showcaseProjects.map((project) => (
                  <motion.div
                    key={project.id}
                    variants={{
                      initial: { opacity: 1, scale: 1 },
                      selected: {
                        opacity: 1,
                        transition: {
                          type: "spring",
                          stiffness: 500,
                          damping: 25,
                        },
                      },
                    }}
                    initial='initial'
                    animate={mobileTab === project.id ? "selected" : "initial"}>
                    <TabsTrigger
                      value={project.id}
                      className='h-[45px] md:h-[50px] text-sm sm:text-lg md:text-2xl data-[state=active]:bg-btn-linear data-[state=active]:text-white rounded-[50px] px-3 sm:px-4 md:px-6 py-1 md:py-2 whitespace-nowrap'>
                      {project.id === "btc-media"
                        ? "BTC Media"
                        : project.id === "3co"
                        ? "3CO"
                        : project.title}
                    </TabsTrigger>
                  </motion.div>
                ))}
              </TabsList>
            </div>

            {/* Desktop Content */}
            <div className='border-gradient-4 rounded-[30px] p-6 overflow-hidden'>
              <AnimatePresence mode='wait'>
                {showcaseProjects.map(
                  (project) =>
                    project.id === mobileTab && (
                      <TabsContent
                        key={project.id}
                        value={project.id}
                        forceMount>
                        <motion.div
                          variants={{
                            hidden: { opacity: 0.2, y: 0 },
                            visible: {
                              opacity: 1,
                              transition: {
                                duration: 0.3,
                                ease: "easeOut",
                              },
                            },
                            exit: {
                              opacity: 0.5,
                              transition: {
                                duration: 0.3,
                                ease: "easeIn",
                              },
                            },
                          }}
                          initial='hidden'
                          animate='visible'
                          exit='exit'>
                          <div>
                            <h3 className='text-lg md:text-[32px] font-bold text-accent mb-2'>
                              {project.title}
                            </h3>
                            <p className='text-sm md:text-xl text-textColor2 mb-2'>
                              {project.description}
                            </p>

                            <div className='flex flex-col lg:flex-row gap-6'>
                              {/* Project Image */}
                              <div className='w-full lg:w-auto mx-auto lg:mx-0'>
                                <Image
                                  src={project.image.src}
                                  alt={project.title}
                                  className={`${
                                    project.id !== "btc-media"
                                      ? "h-[300px] md:h-fit object-contain md:object-cover"
                                      : "object-cover rounded-lg mx-auto"
                                  }`}
                                  width={350}
                                  height={459}
                                  style={
                                    project.id === "btc-media"
                                      ? {
                                          maxWidth: "100%",
                                          height: "auto",
                                          maxHeight: "400px",
                                          objectFit: "cover",
                                        }
                                      : {}
                                  }
                                />
                              </div>

                              {/* Project Details - Right column on desktop */}
                              <div className='flex-1 flex flex-col gap-3 py-3'>
                                {/* Objective Section */}
                                <div className='space-y-3'>
                                  <h3 className='text-lg md:text-2xl font-semibold text-accent'>
                                    OBJECTIVE:
                                  </h3>
                                  <div className='grid grid-cols-2 gap-1'>
                                    {project.objective.map((obj, idx) => (
                                      <div
                                        key={idx}
                                        className='flex items-start gap-2 sm:gap-4'>
                                        <Image
                                          width={24}
                                          height={24}
                                          src={CheckIcon}
                                          alt='check icon'
                                          className='min-w-[24px] h-[24px] mt-1 sm:min-w-[32px] sm:h-[32px]'
                                        />
                                        <div className='font-medium text-base md:text-lg text-textColor2 mt-1.5'>
                                          {obj}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Scope Section */}
                                <div className='space-y-3'>
                                  <h4 className='text-lg md:text-2xl font-semibold text-accent'>
                                    SCOPE:
                                  </h4>
                                  <div className='space-y-1'>
                                    {project.scope.map((scope, idx) => (
                                      <div
                                        key={idx}
                                        className='flex items-start gap-2 sm:gap-4'>
                                        <Image
                                          width={24}
                                          height={24}
                                          src={CheckIcon}
                                          alt='check icon'
                                          className='min-w-[24px] h-[24px] mt-1 sm:min-w-[32px] sm:h-[32px]'
                                        />
                                        <div className='font-medium text-base md:text-lg text-textColor2 mt-1.5'>
                                          {scope}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Results section inside flex layout */}
                                {project.id !== "btc-media" && (
                                  <div>
                                    <h4 className='text-lg md:text-2xl font-semibold text-accent'>
                                      RESULTS:
                                    </h4>
                                    <div
                                      className={cn(
                                        "w-full flex flex-wrap gap-4 md:gap-6 mt-4 text-textColor2",
                                        project.id === "3co" && "flex-wrap"
                                      )}>
                                      {project.results.map((result, idx) => (
                                        <div
                                          key={idx}
                                          className='flex gap-3 sm:gap-5'>
                                          <div className='h-full min-w-[6px] sm:min-w-[9px] bg-linear2'></div>
                                          <div>
                                            <p className='font-medium text-lg md:text-xl'>
                                              {result.value}
                                            </p>
                                            <p className='text-sm md:text-lg'>
                                              {result.label}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Results section for btc-media project */}
                            {project.id === "btc-media" && (
                              <div>
                                <h4 className='text-lg md:text-2xl font-semibold text-accent'>
                                  RESULTS:
                                </h4>
                                <div className='w-full flex flex-wrap md:flex-nowrap gap-4 md:gap-6 mt-4 text-textColor2'>
                                  {project.results.map((result, idx) => (
                                    <div
                                      key={idx}
                                      className='flex gap-3 sm:gap-5'>
                                      <div className='h-full min-w-[6px] sm:min-w-[9px] bg-linear2'></div>
                                      <div>
                                        <p className='font-medium text-lg md:text-xl'>
                                          {result.value}
                                        </p>
                                        <p className='text-sm md:text-lg'>
                                          {result.label}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      </TabsContent>
                    )
                )}
              </AnimatePresence>
            </div>
          </Tabs>
        )}

        {/* Mobile/Tablet Slider View */}
        {isMobile && (
          <div className='w-full px-10 relative'>
            <div
              ref={sliderRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}>
              {/* Slider Navigation Buttons */}
              <div className='hidden md:block absolute top-1/2 -translate-y-1/2 left-2 z-10'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrev();
                  }}
                  disabled={isAnimating}
                  className='w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow-md'
                  aria-label='Previous project'>
                  <ChevronLeft size={20} className='text-accent' />
                </button>
              </div>

              <div className='hidden md:block absolute top-1/2 -translate-y-1/2 right-2 z-10'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  disabled={isAnimating}
                  className='w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow-md'
                  aria-label='Next project'>
                  <ChevronRight size={20} className='text-accent' />
                </button>
              </div>

              {/* Card Slider */}
              <div className='overflow-hidden'>
                <AnimatePresence initial={false} mode='wait'>
                  <motion.div
                    key={mobileTab}
                    variants={slideVariants}
                    initial={
                      swipeDirection === "left" ? "hiddenLeft" : "hiddenRight"
                    }
                    animate='visible'
                    exit={swipeDirection === "left" ? "exitRight" : "exitLeft"}
                    onAnimationComplete={handleAnimationComplete}
                    className='relative'>
                    {/* Project Card */}
                    <div
                      ref={contentRef}
                      onClick={(e) => e.stopPropagation()}
                      className={`border-[5px] border-[#0900F1] rounded-[30px] overflow-hidden p-6 bg-white ${
                        !isCurrentProjectExpanded && isMobile
                          ? "max-h-[600px] overflow-hidden"
                          : ""
                      }`}>
                      <h3 className='text-lg md:text-[32px] font-bold text-accent mb-3'>
                        {currentProject.id === "btc-media"
                          ? "BTC Media"
                          : currentProject.id === "3co"
                          ? "3CO"
                          : currentProject.title}
                      </h3>

                      <p className='text-sm md:text-xl text-textColor2 mb-4'>
                        {currentProject.description}
                      </p>

                      {/* Project Image */}
                      <div className='flex flex-col md:flex-row gap-6'>
                        <Image
                          src={currentProject.image.src}
                          alt={currentProject.title}
                          width={300}
                          height={300}
                          className='mx-auto h-[200px] object-contain'
                        />

                        {/* Details for tablets (md screens) */}
                        <div className=''>
                          {/* RESULTS Section - Always visible */}
                          <div className='mt-4'>
                            <h4 className='text-lg md:text-2xl font-semibold text-accent'>
                              RESULTS:
                            </h4>
                            <div
                              className={cn(
                                "w-full flex flex-wrap lg:flex-nowrap gap-4 md:gap-6 mt-4 text-textColor2",
                                currentProject.id === "btc-media" &&
                                  "flex-wrap",
                                currentProject.id === "3co" &&
                                  "flex-wrap md:flex-nowrap"
                              )}>
                              {currentProject.results.map((result, idx) => (
                                <div key={idx} className='flex gap-3 sm:gap-5'>
                                  <div className='h-full min-w-[6px] sm:min-w-[9px] bg-linear2'></div>
                                  <div>
                                    <p className='font-medium text-lg md:text-xl'>
                                      {result.value}
                                    </p>
                                    <p className='text-sm md:text-lg'>
                                      {result.label}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* OBJECTIVE Section */}
                          <div className=''>
                            <h4 className='text-lg md:text-2xl font-semibold text-accent mb-1'>
                              OBJECTIVE:
                            </h4>
                            <div className='space-y-1'>
                              {currentProject.objective.map((obj, idx) => (
                                <div
                                  key={idx}
                                  className='flex items-start gap-2'>
                                  <Image
                                    width={20}
                                    height={20}
                                    src={CheckIcon}
                                    alt='check'
                                    className='mt-0.5 min-w-[20px]'
                                  />
                                  <p className='text-sm text-gray-700'>{obj}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* SCOPE Section */}
                          <div className='mt-2 '>
                            <h4 className='text-lg md:text-2xl font-semibold text-accent mb-1'>
                              SCOPE:
                            </h4>
                            <div className='space-y-1'>
                              {currentProject.scope.map((scope, idx) => (
                                <div
                                  key={idx}
                                  className='flex items-start gap-2'>
                                  <Image
                                    width={20}
                                    height={20}
                                    src={CheckIcon}
                                    alt='check'
                                    className='mt-0.5 min-w-[20px]'
                                  />
                                  <p className='text-sm text-gray-700'>
                                    {scope}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* View more/less button - only show if content exceeds height threshold */}
                      <div
                        className={`flex justify-center ${
                          !isCurrentProjectExpanded
                            ? "absolute bottom-1 left-0 right-0"
                            : "mt-0 mb-0"
                        }`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand();
                          }}
                          className='flex items-center text-blue-600 font-medium gap-1 bg-white py-2 rounded-full w-[calc(100%-24px)] justify-center'>
                          {isCurrentProjectExpanded ? (
                            <>
                              View less <ChevronUp size={16} />
                            </>
                          ) : (
                            <>
                              View more <ChevronDown size={16} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className='flex justify-center mt-4 space-x-2'>
              {showcaseProjects.map((project, index) => (
                <button
                  key={project.id}
                  onClick={() => handleTabChange(project.id)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    mobileTab === project.id ? "w-4 bg-blue-600" : "bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
